import { Heart, Activity, Droplets, Wind, TrendingUp, TrendingDown, Minus, Calendar, Target, FileText, AlertCircle, Loader2 } from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { useWearableData } from "@/hooks/useWearableData";
import { DeviceConnectionCard } from "@/components/health/DeviceConnectionCard";

// Vital card component
function VitalCard({ 
  icon: Icon, 
  label, 
  value, 
  unit, 
  status, 
  trend,
  trendValue,
  color 
}: { 
  icon: React.ElementType;
  label: string;
  value: string | number;
  unit: string;
  status: "normal" | "warning" | "critical";
  trend: "up" | "down" | "stable";
  trendValue?: string;
  color: string;
}) {
  const statusColors = {
    normal: "bg-success/10 text-success border-success/20",
    warning: "bg-warning/10 text-warning border-warning/20",
    critical: "bg-destructive/10 text-destructive border-destructive/20"
  };

  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;

  return (
    <Card className="border-border/50">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-foreground">{value}</span>
              <span className="text-sm text-muted-foreground">{unit}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={statusColors[status]}>
                {status === "normal" ? "Normal" : status === "warning" ? "Monitor" : "Alert"}
              </Badge>
              {trendValue && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <TrendIcon className="h-3 w-3" />
                  <span>{trendValue}</span>
                </div>
              )}
            </div>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Goal progress card
function GoalCard({ 
  title, 
  current, 
  target, 
  unit,
  progress 
}: { 
  title: string;
  current: number;
  target: number;
  unit: string;
  progress: number;
}) {
  return (
    <div className="space-y-2 p-4 rounded-lg bg-muted/30">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-xs text-muted-foreground">{current} / {target} {unit}</span>
      </div>
      <Progress value={Math.min(progress, 100)} className="h-2" />
      <p className="text-xs text-muted-foreground">
        {progress >= 100 ? "Goal achieved! 🎉" : `${Math.max(0, target - current)} ${unit} to go`}
      </p>
    </div>
  );
}

export default function MemberHealth() {
  const { user } = useAuth();

  // Use the wearable data hook
  const {
    devices,
    latestVitals,
    chartData,
    weeklySummary,
    isLoading,
    hasData,
    syncDevices,
    isSyncing,
    seedHistoricalData,
    isSeeding,
  } = useWearableData();

  // Fetch active care plans
  const { data: carePlans } = useQuery({
    queryKey: ['member-care-plans', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('care_plans')
        .select(`
          *,
          care_plan_goals (*)
        `)
        .eq('member_id', user.id)
        .eq('status', 'active');

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id
  });

  // Get vitals with fallback to placeholder values
  const vitals = latestVitals || {
    heart_rate: null,
    heart_rate_status: null,
    heart_rate_trend: null,
    systolic_bp: null,
    diastolic_bp: null,
    bp_status: null,
    bp_trend: null,
    blood_glucose: null,
    glucose_status: null,
    glucose_trend: null,
    oxygen_saturation: null,
    spo2_status: null,
    spo2_trend: null,
    steps_count: null,
    steps_goal: 10000,
    sleep_hours: null,
    sleep_quality: null,
  };

  // Display chart data
  const displayChartData = chartData.length > 0 ? chartData : [];

  // Summary with fallback
  const summary = weeklySummary || {
    avgHeartRate: 0,
    avgSystolic: 0,
    avgDiastolic: 0,
    avgSteps: 0,
    avgSleep: 0,
    heartRateStatus: "normal" as const,
    bpStatus: "normal" as const,
    stepsStatus: "normal" as const,
    sleepStatus: "normal" as const,
  };

  const statusColorMap = {
    normal: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    critical: "bg-destructive/10 text-destructive",
  };

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground">My Health</h1>
            <p className="mt-1 text-muted-foreground">
              Track your vitals, view health trends, and monitor your care plan progress.
            </p>
          </div>

          {/* Device Connection Card */}
          <DeviceConnectionCard
            devices={devices || []}
            hasData={hasData}
            onSync={syncDevices}
            onSeedData={seedHistoricalData}
            isSyncing={isSyncing}
            isSeeding={isSeeding}
          />

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* No Data State */}
          {!isLoading && !hasData && (
            <Card className="border-border/50 mb-8">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="p-4 rounded-full bg-muted/50 mb-4">
                  <Heart className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">No Health Data Yet</h3>
                <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
                  Connect a wearable device or click "Load Sample Data" to see your health dashboard in action.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Vitals Grid - Show when we have data */}
          {hasData && (
            <>
              <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <VitalCard
                  icon={Heart}
                  label="Heart Rate"
                  value={vitals.heart_rate || "--"}
                  unit="bpm"
                  status={(vitals.heart_rate_status as "normal" | "warning" | "critical") || "normal"}
                  trend="up"
                  trendValue={vitals.heart_rate_trend || undefined}
                  color="bg-destructive/10 text-destructive"
                />
                <VitalCard
                  icon={Activity}
                  label="Blood Pressure"
                  value={vitals.systolic_bp && vitals.diastolic_bp ? `${vitals.systolic_bp}/${vitals.diastolic_bp}` : "--"}
                  unit="mmHg"
                  status={(vitals.bp_status as "normal" | "warning" | "critical") || "normal"}
                  trend="down"
                  trendValue={vitals.bp_trend || undefined}
                  color="bg-primary/10 text-primary"
                />
                <VitalCard
                  icon={Droplets}
                  label="Blood Glucose"
                  value={vitals.blood_glucose || "--"}
                  unit="mg/dL"
                  status={(vitals.glucose_status as "normal" | "warning" | "critical") || "normal"}
                  trend="stable"
                  trendValue={vitals.glucose_trend || undefined}
                  color="bg-warning/10 text-warning"
                />
                <VitalCard
                  icon={Wind}
                  label="Oxygen Saturation"
                  value={vitals.oxygen_saturation || "--"}
                  unit="%"
                  status={(vitals.spo2_status as "normal" | "warning" | "critical") || "normal"}
                  trend="stable"
                  trendValue={vitals.spo2_trend || undefined}
                  color="bg-success/10 text-success"
                />
              </div>

              {/* Tabs for different views */}
              <Tabs defaultValue="trends" className="space-y-6">
                <TabsList>
                  <TabsTrigger value="trends">Health Trends</TabsTrigger>
                  <TabsTrigger value="goals">Goals & Progress</TabsTrigger>
                  <TabsTrigger value="care-plans">Care Plans</TabsTrigger>
                </TabsList>

                {/* Trends Tab */}
                <TabsContent value="trends" className="space-y-6">
                  {displayChartData.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {/* Heart Rate Trend */}
                      <Card className="border-border/50">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Heart className="h-5 w-5 text-destructive" />
                            Heart Rate Trend
                          </CardTitle>
                          <CardDescription>Last 30 days</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={displayChartData}>
                                <defs>
                                  <linearGradient id="heartRateGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <YAxis domain={[50, 100]} className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'hsl(var(--card))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: '8px'
                                  }}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="heartRate" 
                                  stroke="hsl(var(--destructive))" 
                                  fill="url(#heartRateGradient)"
                                  strokeWidth={2}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Blood Pressure Trend */}
                      <Card className="border-border/50">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Activity className="h-5 w-5 text-primary" />
                            Blood Pressure Trend
                          </CardTitle>
                          <CardDescription>Systolic & Diastolic</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={displayChartData}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <YAxis domain={[60, 140]} className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'hsl(var(--card))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: '8px'
                                  }}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="systolic" 
                                  stroke="hsl(var(--primary))" 
                                  strokeWidth={2}
                                  dot={{ fill: 'hsl(var(--primary))' }}
                                  name="Systolic"
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="diastolic" 
                                  stroke="hsl(var(--muted-foreground))" 
                                  strokeWidth={2}
                                  dot={{ fill: 'hsl(var(--muted-foreground))' }}
                                  name="Diastolic"
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Activity Trend */}
                      <Card className="border-border/50 lg:col-span-2">
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Target className="h-5 w-5 text-success" />
                            Daily Steps
                          </CardTitle>
                          <CardDescription>Your activity over time</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-[250px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={displayChartData}>
                                <defs>
                                  <linearGradient id="stepsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                <XAxis dataKey="date" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                                <Tooltip 
                                  contentStyle={{ 
                                    backgroundColor: 'hsl(var(--card))',
                                    border: '1px solid hsl(var(--border))',
                                    borderRadius: '8px'
                                  }}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="steps" 
                                  stroke="hsl(var(--success))" 
                                  fill="url(#stepsGradient)"
                                  strokeWidth={2}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ) : (
                    <Card className="border-border/50">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <p className="text-muted-foreground">No trend data available yet. Sync your device to see trends.</p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Goals Tab */}
                <TabsContent value="goals" className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <Card className="border-border/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Target className="h-5 w-5 text-primary" />
                          Daily Goals
                        </CardTitle>
                        <CardDescription>Track your daily health targets</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <GoalCard 
                          title="Daily Steps"
                          current={vitals.steps_count || 0}
                          target={vitals.steps_goal || 10000}
                          unit="steps"
                          progress={((vitals.steps_count || 0) / (vitals.steps_goal || 10000)) * 100}
                        />
                        <GoalCard 
                          title="Sleep"
                          current={vitals.sleep_hours || 0}
                          target={8}
                          unit="hours"
                          progress={((vitals.sleep_hours || 0) / 8) * 100}
                        />
                        <GoalCard 
                          title="Water Intake"
                          current={6}
                          target={8}
                          unit="glasses"
                          progress={(6 / 8) * 100}
                        />
                      </CardContent>
                    </Card>

                    <Card className="border-border/50">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Calendar className="h-5 w-5 text-primary" />
                          Weekly Summary
                        </CardTitle>
                        <CardDescription>Your health overview this week</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className={`flex items-center justify-between p-3 rounded-lg ${statusColorMap[summary.heartRateStatus]}`}>
                            <div>
                              <p className="font-medium text-foreground">Average Heart Rate</p>
                              <p className="text-sm text-muted-foreground">
                                {summary.heartRateStatus === "normal" ? "Within healthy range" : 
                                 summary.heartRateStatus === "warning" ? "Monitor closely" : "Needs attention"}
                              </p>
                            </div>
                            <span className="text-2xl font-bold">{summary.avgHeartRate || "--"} bpm</span>
                          </div>
                          <div className={`flex items-center justify-between p-3 rounded-lg ${statusColorMap[summary.bpStatus]}`}>
                            <div>
                              <p className="font-medium text-foreground">Average Blood Pressure</p>
                              <p className="text-sm text-muted-foreground">
                                {summary.bpStatus === "normal" ? "Optimal" : 
                                 summary.bpStatus === "warning" ? "Elevated" : "High"}
                              </p>
                            </div>
                            <span className="text-2xl font-bold">
                              {summary.avgSystolic && summary.avgDiastolic 
                                ? `${summary.avgSystolic}/${summary.avgDiastolic}` 
                                : "--"}
                            </span>
                          </div>
                          <div className={`flex items-center justify-between p-3 rounded-lg ${statusColorMap[summary.stepsStatus]}`}>
                            <div>
                              <p className="font-medium text-foreground">Daily Steps Average</p>
                              <p className="text-sm text-muted-foreground">
                                {summary.stepsStatus === "normal" ? "Goal achieved" : 
                                 summary.stepsStatus === "warning" ? "Close to goal" : "Below goal"}
                              </p>
                            </div>
                            <span className="text-2xl font-bold">
                              {summary.avgSteps ? summary.avgSteps.toLocaleString() : "--"}
                            </span>
                          </div>
                          <div className={`flex items-center justify-between p-3 rounded-lg ${statusColorMap[summary.sleepStatus]}`}>
                            <div>
                              <p className="font-medium text-foreground">Sleep Average</p>
                              <p className="text-sm text-muted-foreground">
                                {summary.sleepStatus === "normal" ? "Good quality" : 
                                 summary.sleepStatus === "warning" ? "Could improve" : "Needs attention"}
                              </p>
                            </div>
                            <span className="text-2xl font-bold">{summary.avgSleep || "--"} hrs</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Care Plans Tab */}
                <TabsContent value="care-plans" className="space-y-6">
                  {carePlans && carePlans.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6">
                      {carePlans.map((plan) => (
                        <Card key={plan.id} className="border-border/50">
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="flex items-center gap-2">
                                  <FileText className="h-5 w-5 text-primary" />
                                  {plan.title}
                                </CardTitle>
                                <CardDescription>{plan.description}</CardDescription>
                              </div>
                              <Badge variant="default" className="bg-success/10 text-success border-0">
                                Active
                              </Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span>Started: {format(new Date(plan.start_date), 'MMM d, yyyy')}</span>
                                {plan.target_end_date && (
                                  <span>Target: {format(new Date(plan.target_end_date), 'MMM d, yyyy')}</span>
                                )}
                              </div>
                              {plan.diagnosis && (
                                <p className="text-sm text-muted-foreground">
                                  <span className="font-medium text-foreground">Diagnosis:</span> {plan.diagnosis}
                                </p>
                              )}
                              {plan.care_plan_goals && plan.care_plan_goals.length > 0 && (
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-foreground">Goals:</p>
                                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {plan.care_plan_goals.map((goal: any) => (
                                      <div 
                                        key={goal.id} 
                                        className="flex items-center gap-2 p-2 rounded-lg bg-muted/30"
                                      >
                                        <div className={`w-2 h-2 rounded-full ${
                                          goal.status === 'completed' ? 'bg-success' : 
                                          goal.status === 'in_progress' ? 'bg-primary' : 'bg-muted-foreground'
                                        }`} />
                                        <span className="text-sm">{goal.title}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Card className="border-border/50">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <div className="p-4 rounded-full bg-muted/50 mb-4">
                          <FileText className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium text-foreground mb-2">No Active Care Plans</h3>
                        <p className="text-sm text-muted-foreground text-center max-w-md">
                          You don't have any active care plans at the moment. Your healthcare provider will create 
                          a personalized care plan based on your health needs.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>

              {/* Health Alerts */}
              <Card className="mt-6 border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-warning" />
                    Health Insights
                  </CardTitle>
                  <CardDescription>Personalized recommendations based on your data</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {summary.heartRateStatus === "normal" && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-success/10 border border-success/20">
                        <div className="mt-0.5 p-1 rounded-full bg-success/20">
                          <Heart className="h-4 w-4 text-success" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Great heart health!</p>
                          <p className="text-sm text-muted-foreground">
                            Your resting heart rate has been consistently in the healthy range over the past week.
                          </p>
                        </div>
                      </div>
                    )}
                    {summary.stepsStatus !== "normal" && (
                      <div className="flex items-start gap-3 p-3 rounded-lg bg-warning/10 border border-warning/20">
                        <div className="mt-0.5 p-1 rounded-full bg-warning/20">
                          <Target className="h-4 w-4 text-warning" />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">Step goal reminder</p>
                          <p className="text-sm text-muted-foreground">
                            You're averaging {summary.avgSteps.toLocaleString()} steps daily. Try a short walk after lunch to hit your 10,000 step goal!
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                      <div className="mt-0.5 p-1 rounded-full bg-primary/20">
                        <Calendar className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">Keep syncing your device</p>
                        <p className="text-sm text-muted-foreground">
                          Regular syncing helps track your health trends more accurately. Click "Sync Now" to get the latest data.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
